import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  CalendarDays,
  Check,
  ChevronDown,
  Eye,
  EyeOff,
  Inbox as InboxIcon,
  Link2,
  MessageSquare,
  MessageSquareText,
  Sparkles,
  Unlink,
  UserCheck,
  UserX,
  X,
} from "lucide-react";
import StudyLayout from "../components/StudyLayout";
import Breadcrumbs from "../components/Breadcrumbs";
import NavigationButtons from "../components/NavigationButtons";
import Button from "../components/Button";
import Spinner from "../components/Spinner";
import { connectionsAPI } from "../services/api";
import { useToasts } from "../hooks/useToasts";
import {
  readConnectionsRouteCache,
  writeConnectionsRouteCache,
} from "../utils/connectionsRouteCache";
import "./Connections.css";

const getInitial = (name) => (name || "M").trim().charAt(0).toUpperCase();

const formatMemberSince = (date) => {
  if (!date) return null;
  return new Date(date).toLocaleDateString();
};

const Connections = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { addToast } = useToasts();
  const prefetched = useMemo(
    () => location.state?.prefetchedConnections || readConnectionsRouteCache(),
    [location.state],
  );
  const [loading, setLoading] = useState(!prefetched);
  const [acceptedConnections, setAcceptedConnections] = useState(
    prefetched?.accepted || [],
  );
  const [incomingRequests, setIncomingRequests] = useState(
    prefetched?.incoming || [],
  );
  const [sentRequests, setSentRequests] = useState(prefetched?.sent || []);
  const [messages, setMessages] = useState(prefetched?.messages || []);
  const [hiddenConnections, setHiddenConnections] = useState([]);
  const [hiddenOpen, setHiddenOpen] = useState(false);
  const [hiddenLoading, setHiddenLoading] = useState(false);
  const [actionLoadingKey, setActionLoadingKey] = useState(null);
  const [pendingDisconnectId, setPendingDisconnectId] = useState(null);

  const breadcrumbs = useMemo(
    () => [
      { label: "MySpace", path: "/myspace" },
      {
        label: (
          <span className="connections-breadcrumb-label">
            <Link2 size={14} />
            <span>Connections & Conversations</span>
          </span>
        ),
        path: null,
      },
    ],
    [],
  );

  useEffect(() => {
    const loadAll = async () => {
      try {
        if (!prefetched) setLoading(true);
        const [acceptedRes, incomingRes, sentRes, messagesRes] =
          await Promise.all([
            connectionsAPI.getAcceptedConnections(),
            connectionsAPI.getIncomingRequests(),
            connectionsAPI.getSentRequests(),
            connectionsAPI.getConnections(),
          ]);
        const snapshot = {
          accepted: acceptedRes.data.connections || [],
          incoming: incomingRes.data.requests || [],
          sent: sentRes.data.requests || [],
          messages: messagesRes.data.connections || [],
        };
        setAcceptedConnections(snapshot.accepted);
        setIncomingRequests(snapshot.incoming);
        setSentRequests(snapshot.sent);
        setMessages(snapshot.messages);
        writeConnectionsRouteCache(snapshot);
      } catch (error) {
        addToast(
          error.response?.data?.details?.[0] || "Failed to load connections",
          "error-warning",
        );
      } finally {
        setLoading(false);
      }
    };
    loadAll();
  }, [addToast, prefetched]);

  const handleAccept = async (connectionId) => {
    try {
      setActionLoadingKey(`accept:${connectionId}`);
      await connectionsAPI.acceptConnection(connectionId);
      setIncomingRequests((prev) => prev.filter((r) => r._id !== connectionId));
      const res = await connectionsAPI.getAcceptedConnections();
      setAcceptedConnections(res.data.connections || []);
      addToast("Connection accepted", "success-info");
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to accept",
        "error-warning",
      );
    } finally {
      setActionLoadingKey(null);
    }
  };

  const handleDecline = async (connectionId) => {
    try {
      setActionLoadingKey(`decline:${connectionId}`);
      await connectionsAPI.declineConnection(connectionId);
      setIncomingRequests((prev) => prev.filter((r) => r._id !== connectionId));
      addToast("Connection declined", "success-info");
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to decline",
        "error-warning",
      );
    } finally {
      setActionLoadingKey(null);
    }
  };

  const removeFromList = (connectionId) => {
    setAcceptedConnections((prev) =>
      prev.filter((c) => c._id !== connectionId),
    );
    setIncomingRequests((prev) => prev.filter((c) => c._id !== connectionId));
    setSentRequests((prev) => prev.filter((c) => c._id !== connectionId));
    setMessages((prev) => prev.filter((c) => c._id !== connectionId));
    setHiddenConnections((prev) =>
      prev.filter((c) => c._id !== connectionId),
    );
    window.dispatchEvent(new Event("inbox-changed"));
  };

  const loadHiddenConnections = async () => {
    try {
      setHiddenLoading(true);
      const response = await connectionsAPI.getConnections("hidden");
      setHiddenConnections(response.data.connections || []);
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] ||
          "Failed to load hidden connections",
        "error-warning",
      );
    } finally {
      setHiddenLoading(false);
    }
  };

  const handleToggleHidden = async () => {
    const nextOpen = !hiddenOpen;
    setHiddenOpen(nextOpen);
    if (nextOpen) await loadHiddenConnections();
  };

  const handleHide = async (connectionId) => {
    try {
      setActionLoadingKey(`hide:${connectionId}`);
      await connectionsAPI.hideConnection(connectionId);
      removeFromList(connectionId);
      addToast("Connection hidden", "success-info");
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to hide",
        "error-warning",
      );
    } finally {
      setActionLoadingKey(null);
    }
  };

  const handleUnhide = async (connectionId) => {
    try {
      setActionLoadingKey(`unhide:${connectionId}`);
      await connectionsAPI.unhideConnection(connectionId);
      setHiddenConnections((prev) =>
        prev.filter((c) => c._id !== connectionId),
      );
      const [acceptedRes, incomingRes, sentRes, messagesRes] =
        await Promise.all([
          connectionsAPI.getAcceptedConnections(),
          connectionsAPI.getIncomingRequests(),
          connectionsAPI.getSentRequests(),
          connectionsAPI.getConnections(),
        ]);
      setAcceptedConnections(acceptedRes.data.connections || []);
      setIncomingRequests(incomingRes.data.requests || []);
      setSentRequests(sentRes.data.requests || []);
      setMessages(messagesRes.data.connections || []);
      addToast("Connection visible again", "success-info");
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to unhide",
        "error-warning",
      );
    } finally {
      setActionLoadingKey(null);
    }
  };

  const handleDisconnect = async (connectionId) => {
    try {
      setActionLoadingKey(`disconnect:${connectionId}`);
      await connectionsAPI.disconnectConnection(connectionId);
      const [acceptedRes, messagesRes] = await Promise.all([
        connectionsAPI.getAcceptedConnections(),
        connectionsAPI.getConnections(),
      ]);
      setAcceptedConnections(acceptedRes.data.connections || []);
      setMessages(messagesRes.data.connections || []);
      setPendingDisconnectId(null);
      addToast("Disconnected", "success-info");
    } catch (error) {
      addToast(
        error.response?.data?.details?.[0] || "Failed to disconnect",
        "error-warning",
      );
    } finally {
      setActionLoadingKey(null);
    }
  };

  const acceptedUserIds = useMemo(
    () =>
      new Set(
        acceptedConnections
          .map((connection) => connection.otherUser?.userId)
          .filter(Boolean)
          .map(String),
      ),
    [acceptedConnections],
  );

  const visibleMessages = useMemo(
    () =>
      messages.filter(
        (message) =>
          !acceptedUserIds.has(String(message.otherUser?.userId || "")),
      ),
    [acceptedUserIds, messages],
  );

  const messagesByUserId = useMemo(() => {
    const nextMessagesByUserId = new Map();
    messages.forEach((message) => {
      const userId = message.otherUser?.userId;
      if (userId) nextMessagesByUserId.set(String(userId), message);
    });
    return nextMessagesByUserId;
  }, [messages]);

  const getMessageThreadForPerson = (person) =>
    messagesByUserId.get(String(person?.userId || ""));

  if (loading) return <Spinner visible={true} />;

  const renderCardSlot = (card, label = null, key) => (
    <div className="connections-card-slot" key={key}>
      <h3
        className={`connections-card-group-label ${
          label ? "" : "is-empty"
        }`}
        aria-hidden={label ? undefined : "true"}
      >
        {label || "Group"}
      </h3>
      {card}
    </div>
  );

  const renderPersonCard = (
    person,
    connection,
    { hidden = false, actions, openConnectionId } = {},
  ) => {
    const isPublic = person?.publicProfileVisible === true;
    const memberSince = formatMemberSince(person?.memberSince);
    const profilePath = person?.userId ? `/users/${person.userId}` : null;
    const targetConnectionId = openConnectionId || connection?._id;
    const openConversationAction =
      targetConnectionId && !hidden ? (
        <Button
          className="connections-action-button connections-action-button-round connections-message-button"
          onClick={() => navigate(`/connections/${targetConnectionId}`)}
          title="Open conversation"
          aria-label="Open conversation"
        >
          <MessageSquareText size={15} />
        </Button>
      ) : null;

    return (
      <article
        className={`discover-people-card ${
          connection?.isUnread ? "connections-card-unread" : ""
        }`}
        key={connection?._id}
      >
        <div className="discover-people-card-top">
          <button
            className="discover-people-avatar connections-profile-avatar"
            type="button"
            onClick={() => profilePath && navigate(profilePath)}
            disabled={!profilePath}
            title="View profile"
            aria-label={`View ${person?.publicDisplayName || "member"} profile`}
          >
            {getInitial(person?.publicDisplayName)}
          </button>
          {memberSince ? (
            <span className="discover-people-meta-pill">
              <CalendarDays size={14} />
              {memberSince}
            </span>
          ) : null}
        </div>

        <h2>{person?.publicDisplayName || "Asasiasa member"}</h2>

        {person?.isDeactivated ? (
          <p className="discover-people-bio connections-private-note">
            <EyeOff size={14} />
            This member has self-deactivated their account.
          </p>
        ) : isPublic ? (
          <>
            <p className="discover-people-bio">
              {person.publicBio || "No public bio yet."}
            </p>
            {person.publicInterests ? (
              <div className="discover-people-interests">
                <Sparkles size={14} />
                <span>{person.publicInterests}</span>
              </div>
            ) : null}
          </>
        ) : (
          <p className="discover-people-bio connections-private-note">
            <EyeOff size={14} />
            This profile is private.
          </p>
        )}

        <div className="discover-people-actions connections-card-actions">
          {actions || (
            <div className="connections-card-tools">
              {hidden ? (
                <Button
                  className="connections-action-button connections-action-button-round connections-action-button-muted"
                  onClick={() => handleUnhide(connection._id)}
                  disabled={actionLoadingKey === `unhide:${connection._id}`}
                  title="Unhide"
                  aria-label="Unhide"
                >
                  <Eye size={15} />
                </Button>
              ) : (
                <Button
                  className="connections-action-button connections-action-button-round connections-action-button-muted"
                  onClick={() => handleHide(connection._id)}
                  disabled={actionLoadingKey === `hide:${connection._id}`}
                  title="Hide"
                  aria-label="Hide"
                >
                  <EyeOff size={15} />
                </Button>
              )}
              {connection?.connectionType === "connection" &&
              connection?.status === "accepted" &&
              (pendingDisconnectId === connection._id) ? (
                <div className="connections-inline-confirm">
                  <Button
                    className="connections-action-button connections-action-button-round connections-action-button-muted"
                    onClick={() => setPendingDisconnectId(null)}
                    disabled={
                      actionLoadingKey === `disconnect:${connection._id}`
                    }
                    title="Cancel"
                    aria-label="Cancel"
                  >
                    <X size={15} />
                  </Button>
                  <Button
                    className="connections-action-button connections-action-button-round connections-action-button-danger"
                    onClick={() => handleDisconnect(connection._id)}
                    disabled={
                      actionLoadingKey === `disconnect:${connection._id}`
                    }
                    title="Confirm disconnect"
                    aria-label="Confirm disconnect"
                  >
                    <Check size={15} />
                  </Button>
                </div>
              ) : connection?.connectionType === "connection" &&
                connection?.status === "accepted" ? (
                <Button
                  className="connections-action-button connections-action-button-round connections-action-button-danger"
                  onClick={() => setPendingDisconnectId(connection._id)}
                  disabled={
                    actionLoadingKey === `hide:${connection._id}` ||
                    actionLoadingKey === `unhide:${connection._id}`
                  }
                  title="Disconnect"
                  aria-label="Disconnect"
                >
                  <Unlink size={15} />
                </Button>
              ) : null}
              {openConversationAction}
            </div>
          )}
        </div>
      </article>
    );
  };

  const connectionCards = [
    ...incomingRequests.map((req, index) =>
      renderCardSlot(
        renderPersonCard(req.otherUser, req, {
          openConnectionId: getMessageThreadForPerson(req.otherUser)?._id,
          actions: (
            <div className="connections-card-tools">
              <Button
                className="connections-action-button connections-action-button-round connections-action-button-muted"
                onClick={() => handleHide(req._id)}
                disabled={actionLoadingKey === `hide:${req._id}`}
                title="Hide"
                aria-label="Hide"
              >
                <EyeOff size={15} />
              </Button>
              <Button
                className="connections-action-button connections-action-button-round"
                onClick={() => handleAccept(req._id)}
                disabled={actionLoadingKey === `accept:${req._id}`}
                title="Accept"
                aria-label="Accept"
              >
                <UserCheck size={15} />
              </Button>
              <Button
                className="connections-action-button connections-action-button-round connections-action-button-danger"
                onClick={() => handleDecline(req._id)}
                disabled={actionLoadingKey === `decline:${req._id}`}
                title="Decline"
                aria-label="Decline"
              >
                <UserX size={15} />
              </Button>
              {getMessageThreadForPerson(req.otherUser)?._id ? (
                <Button
                  className="connections-action-button connections-action-button-round connections-message-button"
                  onClick={() =>
                    navigate(
                      `/connections/${
                        getMessageThreadForPerson(req.otherUser)._id
                      }`,
                    )
                  }
                  title="Open conversation"
                  aria-label="Open conversation"
                >
                  <MessageSquareText size={15} />
                </Button>
              ) : null}
            </div>
          ),
        }),
        index === 0 ? `Incoming Requests (${incomingRequests.length})` : null,
        `incoming:${req._id}`,
      ),
    ),
    ...sentRequests.map((req, index) =>
      renderCardSlot(
        renderPersonCard(req.otherUser, req, {
          openConnectionId: getMessageThreadForPerson(req.otherUser)?._id,
          actions: (
            <div className="connections-card-tools">
              <Button
                className="connections-action-button connections-action-button-round connections-action-button-muted"
                onClick={() => handleHide(req._id)}
                disabled={actionLoadingKey === `hide:${req._id}`}
                title="Hide"
                aria-label="Hide"
              >
                <EyeOff size={15} />
              </Button>
              {getMessageThreadForPerson(req.otherUser)?._id ? (
                <Button
                  className="connections-action-button connections-action-button-round connections-message-button"
                  onClick={() =>
                    navigate(
                      `/connections/${
                        getMessageThreadForPerson(req.otherUser)._id
                      }`,
                    )
                  }
                  title="Open conversation"
                  aria-label="Open conversation"
                >
                  <MessageSquareText size={15} />
                </Button>
              ) : null}
              <span className="connections-pending-badge">Pending</span>
            </div>
          ),
        }),
        index === 0 ? `Sent Requests (${sentRequests.length})` : null,
        `sent:${req._id}`,
      ),
    ),
    ...acceptedConnections.map((connection, index) =>
      renderCardSlot(
        renderPersonCard(connection.otherUser, connection),
        index === 0 ? `Connections (${acceptedConnections.length})` : null,
        `accepted:${connection._id}`,
      ),
    ),
  ];

  return (
    <StudyLayout
      breadcrumbs={<Breadcrumbs path={breadcrumbs} />}
      buttons={<NavigationButtons />}
    >
      <div className="connections-page">
        {/* Section 1: Connections (bilateral) */}
        <section className="connections-flow">
          {connectionCards.length > 0 ? (
            <div className="connections-list">{connectionCards}</div>
          ) : (
            <div className="connections-empty">
              <Link2 size={20} />
              <span>No connections yet.</span>
            </div>
          )}
        </section>

        {/* Section 2: Messages (existing flow) */}
        <section className="connections-flow">
          <h2 className="connections-simple-title">Conversations</h2>

          {visibleMessages.length === 0 ? (
            <div className="connections-empty">
              <MessageSquare size={20} />
              <span>No conversations yet.</span>
            </div>
          ) : (
            <div className="connections-list">
              {visibleMessages.map((c) => renderPersonCard(c.otherUser, c))}
            </div>
          )}
        </section>

        {/* Section 3: Hidden */}
        <section className="connections-hidden-section">
          <button
            className="connections-hidden-toggle"
            type="button"
            onClick={handleToggleHidden}
            aria-expanded={hiddenOpen}
          >
            <span>Hidden</span>
            <ChevronDown
              className={`connections-hidden-chevron ${
                hiddenOpen ? "is-open" : ""
              }`}
              size={16}
            />
          </button>
          {hiddenOpen ? (
            hiddenLoading ? (
              <div className="connections-empty">
                <InboxIcon size={20} />
                <span>Loading...</span>
              </div>
            ) : hiddenConnections.length === 0 ? (
              <div className="connections-empty">
                <EyeOff size={20} />
                <span>Nothing hidden.</span>
              </div>
            ) : (
              <div className="connections-list">
                {hiddenConnections.map((c) =>
                  renderPersonCard(c.otherUser, c, { hidden: true }),
                )}
              </div>
            )
          ) : null}
        </section>

        <section
          className="connections-about"
          aria-labelledby="connections-about-title"
        >
          <h2 id="connections-about-title">About Connections</h2>
          <p>
            Connections are mutual — both users must accept. Messages are
            one-way notes. Private, not realtime chat. Notes are limited per
            day based on your plan.
          </p>
        </section>
      </div>
    </StudyLayout>
  );
};

export default Connections;
